import { ActorRef, assign, createMachine, Interpreter, sendParent, State } from 'xstate'
import { CartFieldsFragment, Scalars, ShippingMethodsQuery } from 'types/graphql'

export type ShippingMethodContext = {
  cartId: Scalars['ID'] | null
  bagId: number | null
  shippingMethods: ShippingMethodsQuery['cartShippingMethods'][0]['shipping_methods']
  selectedShippingMethodId?: string | null
  containsPhysicalProducts: boolean | null
}

type ShippingMethodState =
  | {
      value: 'ready'
      context: ShippingMethodContext
    }
  | {
      value: 'selectingShippingMethod'
      context: ShippingMethodContext
    }
  | {
      value: 'selected'
      context: ShippingMethodContext & {
        selectedShippingMethodId: string
      }
    }

type SetShippingMethodAction = { type: 'SELECT_SHIPPING_METHOD'; shippingMethodId: string }

type ShippingMethodsEvent = SetShippingMethodAction

type ShippingMethodServices = {
  selectShippingMethod: {
    data: CartFieldsFragment
  }
}

export type ShippingMethodService = Interpreter<ShippingMethodContext, any, ShippingMethodsEvent, ShippingMethodState>
export type ShippingMethodActor = ActorRef<ShippingMethodsEvent, State<ShippingMethodContext, ShippingMethodsEvent>>

export type SetShippingMethodService = { cart: CartFieldsFragment }

export const shippingMethodMachine = createMachine(
  {
    tsTypes: {} as import('./shippingMethodMachine.typegen').Typegen0,
    predictableActionArguments: true,
    schema: {
      context: {} as ShippingMethodContext,
      events: {} as ShippingMethodsEvent,
      services: {} as ShippingMethodServices
    },
    id: 'shippingMethodMachine',
    context: {
      cartId: null,
      bagId: null,
      shippingMethods: [],
      selectedShippingMethodId: null,
      containsPhysicalProducts: null
    },
    initial: 'ready',
    states: {
      ready: {
        always: [
          {
            target: 'selected',
            cond: 'shippingMethodSelected',
            actions: ['sendPreselectedShippingMethodToParent']
          },
          {
            target: 'selected',
            cond: 'hasOneShippingMethod',
            actions: ['setDefaultShippingMethod', 'sendPreselectedShippingMethodToParent']
          },
          {
            target: 'selected',
            cond: 'hasNoPhysicalProduct',
            actions: ['sendShippingMethodIdToParent']
          }
        ],
        on: {
          SELECT_SHIPPING_METHOD: {
            target: 'selected',
            actions: ['setSelectedShippingMethodId', 'sendShippingMethodIdToParent']
          }
        }
      },
      selected: {
        on: {
          SELECT_SHIPPING_METHOD: {
            actions: ['setSelectedShippingMethodId', 'sendShippingMethodIdToParent']
          }
        }
      }
    }
  },
  {
    actions: {
      setSelectedShippingMethodId: assign({
        selectedShippingMethodId: (_, { shippingMethodId }) => shippingMethodId
      }),

      sendShippingMethodIdToParent: sendParent(({ bagId }, event) => ({
        type: 'SELECT_SHIPPING_METHOD',
        bagId,
        shippingMethodId: event.type === 'SELECT_SHIPPING_METHOD' ? event.shippingMethodId : null
      })),

      sendPreselectedShippingMethodToParent: sendParent(({ bagId, selectedShippingMethodId }) => ({
        type: 'SELECT_SHIPPING_METHOD',
        bagId,
        shippingMethodId: selectedShippingMethodId
      })),

      setDefaultShippingMethod: assign((context) => ({
        selectedShippingMethodId: context.shippingMethods[0].shipping_method_id
      }))
    },
    guards: {
      shippingMethodSelected: (context) => {
        return !!context.selectedShippingMethodId
      },
      hasOneShippingMethod: (context) => {
        return context.shippingMethods.length === 1
      },
      hasNoPhysicalProduct: (context) => !context.containsPhysicalProducts
    }
  }
)
